import type { Filter_1_0_0, Sort_1_0_0 } from '@luxuryescapes/contract-data-event-schemas/lib/com.luxgroup'
import * as comluxgroup from '@luxuryescapes/contract-data-event-schemas/lib/com.luxgroup'
import { clientEvent, type SnowplowEvent } from 'analytics/snowplow/events'
import {
  isLocalDestinationOfferAllFilter,
  LOCAL_DESTINATION_OFFER_ALL_DESTINATION_NAME,
  LOCAL_DESTINATION_OFFER_ALL_FILTER_VALUE,
} from './localDestinationOfferCarouselConstants'

export const LOCAL_DESTINATION_OFFER_CAROUSEL_MODULE_ID = 'local-destination-hotel-carousel'
export const LOCAL_DESTINATION_OFFER_MPV_RANKING_SOURCE = 'light-search-default-mpv'
export const LOCAL_DESTINATION_OFFER_ALL_MPV_RANKING_SOURCE = 'hotel-stream-default-mpv'
export const LOCAL_DESTINATION_OFFER_CAROUSEL_CHIP_CHANGE_SUBJECT = 'local-destination-hotel-carousel-destination-filter'
export { LOCAL_DESTINATION_OFFER_ALL_DESTINATION_NAME }

export type LocalDestinationChipChangeSource = 'user' | 'programmatic'

export function shouldEmitLocalDestinationChipChangeAnalytics(
  source?: LocalDestinationChipChangeSource,
): boolean {
  return source === 'user'
}

export function resolvePendingLocalDestinationChipAnalyticsDestinationId(
  value: string | undefined,
  selectedFilter: string,
  source?: LocalDestinationChipChangeSource,
): string | undefined {
  if (!shouldEmitLocalDestinationChipChangeAnalytics(source)) {
    return undefined
  }

  if (value === undefined || value === selectedFilter) {
    return undefined
  }

  return value
}

export function shouldDispatchLocalDestinationChipChangeAnalytics(
  selectedFilter: string | undefined,
  pendingDestinationId: string | undefined,
): boolean {
  return !!selectedFilter && pendingDestinationId === selectedFilter
}

export type LocalDestinationOfferCarouselAnalyticsData = {
  marketCode: string
  destinationId: string
  destinationName: string
  destinationRank: number
  moduleIdentifier: string
  mpvRankingSource: string
}

export function isLocalDestinationOfferCarouselAnalyticsData(
  value: unknown,
): value is LocalDestinationOfferCarouselAnalyticsData {
  if (!value || typeof value !== 'object') {
    return false
  }

  const data = value as Partial<LocalDestinationOfferCarouselAnalyticsData>
  return !!data.marketCode
    && !!data.destinationId
    && !!data.destinationName
    && typeof data.destinationRank === 'number'
    && !!data.moduleIdentifier
    && !!data.mpvRankingSource
}

export function buildLocalDestinationOfferCarouselFilterContexts(
  data: LocalDestinationOfferCarouselAnalyticsData,
): Array<Filter_1_0_0> {
  return [
    {
      type: 'Market',
      label: 'Market',
      value: data.marketCode,
    },
    {
      type: 'Destination',
      label: data.destinationName,
      value: data.destinationId,
    },
    {
      type: 'DestinationRank',
      label: 'Destination rank',
      value: String(data.destinationRank),
    },
    {
      type: 'Module',
      label: 'Module',
      value: data.moduleIdentifier,
    },
    {
      type: 'MpvRankingSource',
      label: 'MPV ranking source',
      value: data.mpvRankingSource,
    },
  ]
}

/**
 * Snowplow Sort schema only accepts generic sort values such as `recommended`.
 * MPV ranking for this module is tracked via the `MpvRankingSource` filter context,
 * not via Sort.
 */
export function buildLocalDestinationOfferCarouselSortContext(): Sort_1_0_0 {
  return {
    value: 'recommended',
  }
}

interface BuildAnalyticsDataOptions {
  marketCode: string
  /** Full trending destination order used for analytics destination rank */
  trendingDestinations: Array<{ destinationId: string, name: string }>
  /** Eligible destinations shown in carousel chips; used to resolve the active destination */
  eligibleDestinations: Array<{ destinationId: string, name: string }>
  selectedDestinationId?: string
  defaultDestinationId?: string
}

function resolveActiveLocalDestinationId(
  options: BuildAnalyticsDataOptions,
): string | undefined {
  const { selectedDestinationId, defaultDestinationId, eligibleDestinations } = options

  if (isLocalDestinationOfferAllFilter(selectedDestinationId) && eligibleDestinations.length > 0) {
    return selectedDestinationId
  }

  if (
    selectedDestinationId !== undefined
    && eligibleDestinations.some(destination => destination.destinationId === selectedDestinationId)
  ) {
    return selectedDestinationId
  }

  if (isLocalDestinationOfferAllFilter(defaultDestinationId) && eligibleDestinations.length > 0) {
    return defaultDestinationId
  }

  return defaultDestinationId
}

export function buildLocalDestinationOfferCarouselAnalyticsData(
  options: BuildAnalyticsDataOptions,
): LocalDestinationOfferCarouselAnalyticsData | undefined {
  const activeDestinationId = resolveActiveLocalDestinationId(options)

  if (isLocalDestinationOfferAllFilter(activeDestinationId)) {
    if (options.eligibleDestinations.length === 0) {
      return undefined
    }

    return {
      marketCode: options.marketCode,
      destinationId: LOCAL_DESTINATION_OFFER_ALL_FILTER_VALUE,
      destinationName: LOCAL_DESTINATION_OFFER_ALL_DESTINATION_NAME,
      destinationRank: 0,
      moduleIdentifier: LOCAL_DESTINATION_OFFER_CAROUSEL_MODULE_ID,
      mpvRankingSource: LOCAL_DESTINATION_OFFER_ALL_MPV_RANKING_SOURCE,
    }
  }

  const destination = options.eligibleDestinations.find(item => item.destinationId === activeDestinationId)
  const destinationRank = options.trendingDestinations.findIndex(item => item.destinationId === activeDestinationId) + 1

  if (!destination?.destinationId || destinationRank <= 0) {
    return undefined
  }

  return {
    marketCode: options.marketCode,
    destinationId: destination.destinationId,
    destinationName: destination.name,
    destinationRank,
    moduleIdentifier: LOCAL_DESTINATION_OFFER_CAROUSEL_MODULE_ID,
    mpvRankingSource: LOCAL_DESTINATION_OFFER_MPV_RANKING_SOURCE,
  }
}

export function buildLocalDestinationChipChangeClientEvent(
  params: App.OfferListTrackingConfig,
  data: LocalDestinationOfferCarouselAnalyticsData,
): SnowplowEvent {
  const filterContexts = buildLocalDestinationOfferCarouselFilterContexts(data)
  const sortContext = buildLocalDestinationOfferCarouselSortContext()

  return clientEvent({
    subject: LOCAL_DESTINATION_OFFER_CAROUSEL_CHIP_CHANGE_SUBJECT,
    action: 'selected',
    category: params.listName,
    type: 'interaction',
    context: [
      ...filterContexts.map(filterContext => comluxgroup.createFilter_1_0_0({
        ...filterContext,
      })),
      comluxgroup.createSort_1_0_0({
        ...sortContext,
      }),
      comluxgroup.createListItem_1_0_0({
        listInstance: params.listId,
        listName: params.listName,
        itemPosition: data.destinationRank,
      }),
    ],
  })
}
